import Layout from '@/components/Layout';
import LargeTourItem from '@/components/LargeTourItem';
import TourTypeContentPage from '@/components/TourTypeContent';
import { getMetaDataPage, getTourTypeContent, PageMetaOutput } from '@/services/directus';
import { tourService } from '@/services/tourService';
import { Tour } from '@/types/tours';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Head from 'next/head';
import { ReactElement } from 'react';
import { TourType } from '@/types/tourBooking';

type AdventuresPageProps = {
  content: any;
  leftContentHtml: string;
  rightContentHtml: string;
  tours: Tour[];
  metadata: PageMetaOutput;
};

const AdventuresPage = ({
  content,
  leftContentHtml,
  rightContentHtml,
  tours,
  metadata,
}: AdventuresPageProps) => {
  return (
    <>
      <Head>
        <title>{metadata.title}</title>
        <meta name="description" content={metadata.description} />
      </Head>
      <TourTypeContentPage
        content={content}
        leftContentHtml={leftContentHtml}
        rightContentHtml={rightContentHtml}
        sectionBackgroundColor="#012F5B"
      />
      <section>
        {tours.map((tour, index) => (
          <LargeTourItem
            key={tour.id}
            tour={tour}
            index={index}
            tourType="adventures"
          />
        ))}
      </section>
    </>
  );
};

AdventuresPage.getLayout = (page: ReactElement) => (
  <Layout showTabs hasFooter hasPadding={false} headerBackgroundColor="#012F5B">
    {page}
  </Layout>
);

AdventuresPage.type = 'public';

export async function getServerSideProps({ locale }: { locale: string }) {
  const lang = locale ?? 'en';
  const pageType = 'adventure_tours';
  const i18nProps = await serverSideTranslations(lang, ['common', 'validate', 'aboutUs']);
  const normalizedLang = lang.split('-')[0];
  const i18nStore = (i18nProps as any)?._nextI18Next?.initialI18nStore;
  const localizedFallbackTitle =
    i18nStore?.[lang]?.common?.adventureTours?.metaTitle ??
    i18nStore?.[normalizedLang]?.common?.adventureTours?.metaTitle;
  const localizedFallbackDescription =
    i18nStore?.[lang]?.common?.adventureTours?.metaDescription ??
    i18nStore?.[normalizedLang]?.common?.adventureTours?.metaDescription;
  const fallbackTitle =
    typeof localizedFallbackTitle === 'string' && localizedFallbackTitle.trim()
      ? localizedFallbackTitle
      : 'HoiAnGO Adventure Tours in Hoi An';
  const fallbackDescription =
    typeof localizedFallbackDescription === 'string' && localizedFallbackDescription.trim()
      ? localizedFallbackDescription
      : 'Discover the highlights of Hoi An with a HoiAnGO Sightseeing Tour. Choose from Heritage, Countryside, Nature & Village Adventures with your private E-Car and driver.';
  let metadata: PageMetaOutput = {
    title: fallbackTitle,
    description: fallbackDescription,
  };
  let leftContentHtml = '';
  let rightContentHtml = '';
  let content = null;
  let tours: Tour[] = [];

  try {
    metadata = await getMetaDataPage({
      type: pageType,
      fallbackTitle,
      fallbackDescription,
    });
  } catch (error) {
    console.error('[adventures] failed to load metadata', error);
  }

  try {
    content = await getTourTypeContent({
      lang,
      type: pageType,
    });
    leftContentHtml = content?.leftContentHtml ?? '';
    rightContentHtml = content?.rightContentHtml ?? '';
  } catch (error) {
    console.error('[adventures] failed to load page content', error);
  }

  try {
    tours = await tourService.fetchTours({
      lang,
      type: TourType.ADVENTURES,
    });
  } catch (error) {
    console.error('[adventures] failed to load tours', error);
  }
  return {
    props: {
      content: content?.translation,
      leftContentHtml,
      rightContentHtml,
      tours,
      metadata,
      ...i18nProps,
    },
  };
}

export default AdventuresPage;
